<template>
    <div class="flex flex-col gap-3 items-start mt-4">
        <ChatLoadingFrame v-if="!hideStatusLabel" :is-loading="!tool.completed">
            {{ label }}
        </ChatLoadingFrame>
        <Transition name="fade">
            <component :is="toolComponent" v-if="tool.completed" v-bind="tool" :hide-email-button="hideEmailButton" />
        </Transition>
    </div>
</template>

<script setup>
import { ACTION } from "~vue/chatActions.js";
import ChatAgentToolActionItems from "~vue/ChatDetail/ChatAgentToolActionItems.vue";
import ChatAgentToolDocumentCreator from "~vue/ChatDetail/ChatAgentToolDocumentCreator.vue";
import ChatAgentToolEmailChatSummary from "~vue/ChatDetail/ChatAgentToolEmailChatSummary.vue";
import ChatAgentToolFollowup from "~vue/ChatDetail/ChatAgentToolFollowup.vue";
import ChatAgentToolLearningResource from "~vue/ChatDetail/ChatAgentToolLearningResource.vue";
import ChatPerformancePicker from "~vue/ChatDetail/ChatPerformancePicker.vue";
import ChatLoadingFrame from "~vue/ChatLoadingFrame.vue";
import { computed } from "vue";

const props = defineProps({
    tool: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    hideEmailButton: {
        type: Boolean,
        default: false,
    },
});

// The picker card is self-explanatory — once it renders, a "checked" pill above it is noise.
// The loading label still shows while the tool runs.
const hideStatusLabel = computed(() => props.tool.completed && [ACTION.PM_PICKER, ACTION.SET_UP_DEVELOPMENT_GOAL].includes(props.tool.name));

const toolComponent = computed(() => {
    switch (props.tool.name) {
        case ACTION.FOLLOW_UP:
            return ChatAgentToolFollowup;
        case ACTION.EMAIL_CHAT_SUMMARY:
            return ChatAgentToolEmailChatSummary;
        case ACTION.ACTION_ITEMS:
            return ChatAgentToolActionItems;
        case ACTION.DOCUMENT_CREATOR:
            return ChatAgentToolDocumentCreator;
        case ACTION.RECOMMEND_LEARNING_CONTENT:
            return ChatAgentToolLearningResource;
        case ACTION.PM_PICKER:
            return ChatPerformancePicker;
    }

    return null;
});

const label = computed(() => {
    if (props.tool.completed) {
        switch (props.tool.name) {
            case ACTION.FOLLOW_UP:
                return "Invite generated";
            case ACTION.EMAIL_CHAT_SUMMARY:
                return "Summary generated";
            case ACTION.ACTION_ITEMS:
                return "Action item generated";
            case ACTION.DOCUMENT_CREATOR:
                return "Document generated";
            case ACTION.RECOMMEND_LEARNING_CONTENT:
                return "Learning resource found";
            case "list_meetings":
                return "Meetings fetched";
            case "get_meeting_content":
                return "Meeting details reviewed";
            case "get_meeting_transcript":
                return "Meeting transcript reviewed";
            case ACTION.WORKIQ_COLLABORATION_CONTEXT:
                return "Understanding your recent collaboration";
            case ACTION.WORKIQ_MY_WORK_CONTEXT:
                return "Understanding your recent work";
            default:
                return "Completed";
        }
    }

    // Live progress text (e.g. Work IQ's own "Checking your calendar..." lifecycle
    // messages) takes priority over the static per-tool label while running.
    if (props.tool.statusText) {
        return props.tool.statusText;
    }

    switch (props.tool.name) {
        case ACTION.FOLLOW_UP:
            return "Generating your invite";

        case ACTION.EMAIL_CHAT_SUMMARY:
            return "Generating a summary";

        case ACTION.ACTION_ITEMS:
            return "Generating your action item";

        case ACTION.DOCUMENT_CREATOR:
            return "Generating your document";

        case ACTION.RECOMMEND_LEARNING_CONTENT:
            return "Finding a learning resource...";

        case "list_meetings":
            return "Fetching meetings...";

        case "get_meeting_content":
            return "Reading meeting details...";

        case "get_meeting_transcript":
            return "Reading meeting transcript...";

        case ACTION.WORKIQ_COLLABORATION_CONTEXT:
            return "Checking recent conversations...";

        case ACTION.WORKIQ_MY_WORK_CONTEXT:
            return "Reviewing your recent work...";

        case ACTION.PM_PICKER:
            return "Looking up your performance tasks...";

        default:
            return "Running...";
    }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 300ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

i.bi::before {
    font-weight: bold !important;
}
</style>
